import React, { forwardRef, useState } from 'react';
import { cn } from '../../utils/cn';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children?: React.ReactNode;
  ripple?: boolean;
  hover?: 'lift' | 'glow' | 'scale' | 'none';
  press?: 'scale' | 'ripple' | 'none';
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      children,
      className,
      disabled,
      ripple = true,
      hover = 'lift',
      press = 'scale',
      type = 'button',
      onKeyDown,
      onKeyUp,
      ...props
    },
    ref,
  ) => {
    const [isPressed, setIsPressed] = useState(false);
    const [rippleEffect, setRippleEffect] = useState<{ x: number; y: number } | null>(null);

    const baseClasses = 'btn';

    const variantClasses = {
      primary: 'btn-primary',
      secondary: 'btn-secondary',
      ghost: 'btn-ghost',
      danger: 'btn-danger',
    };

    const sizeClasses = {
      sm: 'btn-sm',
      md: '', // Default size
      lg: 'btn-lg',
    };

    const hoverClasses = {
      lift: 'hover-lift',
      glow: 'glow',
      scale: 'hover:scale-105',
      none: '',
    };

    const pressClasses = {
      scale: 'btn-press',
      ripple: 'ripple',
      none: '',
    };

    const isDisabled = disabled || loading;

    const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (ripple && !isDisabled) {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        setRippleEffect({ x, y });
        setTimeout(() => setRippleEffect(null), 600);
      }
      setIsPressed(true);
    };

    const handleMouseUp = () => {
      setIsPressed(false);
    };

    const handleMouseLeave = () => {
      setIsPressed(false);
    };

    return (
      <button
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          hoverClasses[hover],
          pressClasses[press],
          {
            'opacity-50 cursor-not-allowed': isDisabled,
            'relative overflow-hidden': loading || ripple,
            'transform scale-95': isPressed && press === 'scale',
          },
          'transition-all duration-200 ease-spring',
          className,
        )}
        type={type}
        disabled={isDisabled}
        aria-busy={loading}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onKeyDown={(event) => {
          if (event.key === ' ' || event.key === 'Enter') {
            setIsPressed(true);
          }
          onKeyDown?.(event);
        }}
        onKeyUp={(event) => {
          if (event.key === ' ' || event.key === 'Enter') {
            setIsPressed(false);
          }
          onKeyUp?.(event);
        }}
        {...props}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-inherit">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {ripple && rippleEffect && (
          <div
            className="absolute pointer-events-none animate-ripple"
            style={{
              left: rippleEffect.x,
              top: rippleEffect.y,
              width: '0',
              height: '0',
            }}
          />
        )}

        <div className={cn('flex items-center gap-2', { 'opacity-0': loading })}>
          {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
          {children && <span>{children}</span>}
          {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
        </div>
      </button>
    );
  },
);

Button.displayName = 'Button';

export { Button };
